import { DealAmountMappingValue } from '@monetize/types/app';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useState } from 'react';
import { BsArrowRight } from 'react-icons/bs';
import {
  useGetCrmFields,
  useGetMonetizeNowCustomFields,
  useGetUserDefinedCrmFieldMappings,
  useSaveDealAmountMapping,
} from '../../../../../../api/fieldMappingService';
import {
  MButton,
  MCard,
  MCardBody,
  MCardFooter,
  MDivider,
  MFlex,
  MFormField,
  MIcon,
  MSkeleton,
  MText,
} from '../../../../../../components/Monetize';
import { MCustomSelect } from '../../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { useFlags } from '../../../../../../services/launchDarkly';
import {
  buildPlaceholderCrmFields,
  mapExistingFieldMapping,
  mapExistingHubSpotFieldMappingFromMn,
  mapMatchingHubSpotFields,
} from '../../../../../../utils/crmFieldMapping';
import { CrmFieldMappingAccordion } from '../fieldmapping/CrmFieldMappingAccordion';
import { CrmFieldsUnavailableAlert } from '../fieldmapping/CrmFieldsUnavailableAlert';

const DEAL_AMOUNT_MAPPING_OPTIONS: {
  title: string;
  value: DealAmountMappingValue;
}[] = [
  { title: 'Amount', value: 'AMOUNT' },
  { title: 'ARR', value: 'ARR' },
  { title: 'Incremental ARR', value: 'INCREMENTAL_ARR' },
  { title: 'None', value: 'NONE' },
];

export const HubSpotFieldMapping = ({
  existingDealAmountMap,
}: {
  existingDealAmountMap?: DealAmountMappingValue;
}) => {
  const { enableProductCatalogSyncToHubspot } = useFlags();
  const [dealAmountMapping, setDealAmountMapping] = useState<
    DealAmountMappingValue | ''
  >(existingDealAmountMap ?? '');
  const [savedDealAmountMapping, setSavedDealAmountMapping] = useState<
    DealAmountMappingValue | ''
  >(existingDealAmountMap ?? '');

  useEffect(() => {
    if (existingDealAmountMap !== undefined) {
      setDealAmountMapping(existingDealAmountMap);
      setSavedDealAmountMapping(existingDealAmountMap);
    }
  }, [existingDealAmountMap]);

  const {
    mutate: saveDealAmountMapping,
    isPending: isSavingDealAmountMapping,
  } = useSaveDealAmountMapping();

  const handleDealAmountMapping = () => {
    if (dealAmountMapping) {
      saveDealAmountMapping(dealAmountMapping, {
        onSuccess: () => setSavedDealAmountMapping(dealAmountMapping),
      });
    }
  };

  const {
    data: fetchedHubSpotFields,
    isLoading: isHubspotFieldLoading,
    isError: isHubSpotFieldsError,
  } = useGetCrmFields('hubspot');
  const { monetizeNowFields, isLoading: isMnFieldLoading } =
    useGetMonetizeNowCustomFields('hubspot');

  const {
    data: userDefinedFieldMappings,
    isLoading: isUserDefinedFieldMappingLoading,
  } = useGetUserDefinedCrmFieldMappings('hubspot');

  const hubSpotFields = isHubSpotFieldsError
    ? buildPlaceholderCrmFields('hubspot', userDefinedFieldMappings)
    : fetchedHubSpotFields;

  const { companyToAccount, contactToContact, dealToQuote, productToProducts } =
    mapMatchingHubSpotFields(hubSpotFields, monetizeNowFields);

  const {
    userDefinedCompaniesToAccount,
    userDefinedContactsToContact,
    userDefinedDealsToQuote,
  } = mapExistingFieldMapping(
    userDefinedFieldMappings,
    hubSpotFields,
    monetizeNowFields,
  );

  const { userDefinedProductToProducts } = mapExistingHubSpotFieldMappingFromMn(
    userDefinedFieldMappings,
    hubSpotFields,
    monetizeNowFields,
  );

  const isLoading =
    isHubspotFieldLoading ||
    isMnFieldLoading ||
    isUserDefinedFieldMappingLoading;

  const renderAccordionItems = (
    <>
      {!isEmpty(companyToAccount) && (
        <CrmFieldMappingAccordion
          system="hubspot"
          isReadOnly={isHubSpotFieldsError}
          crmObjectType="companies"
          crmObjectTypeLabel={'Company'}
          mnObjectType="Account"
          matchedFields={companyToAccount}
          userDefinedFields={userDefinedCompaniesToAccount}
        />
      )}
      {!isEmpty(contactToContact) && (
        <CrmFieldMappingAccordion
          system="hubspot"
          isReadOnly={isHubSpotFieldsError}
          crmObjectType="contacts"
          crmObjectTypeLabel={'Contact'}
          mnObjectType="Contact"
          matchedFields={contactToContact}
          userDefinedFields={userDefinedContactsToContact}
        />
      )}
      {!isEmpty(dealToQuote) && (
        <CrmFieldMappingAccordion
          system="hubspot"
          isReadOnly={isHubSpotFieldsError}
          crmObjectType="deals"
          crmObjectTypeLabel={'Deal'}
          mnObjectType="Quote"
          matchedFields={dealToQuote}
          userDefinedFields={userDefinedDealsToQuote}
        />
      )}
    </>
  );
  return (
    <>
      <MFlex gap="4" direction="column">
        <MFlex color="tPurple.dark" direction="inherit" gap="2">
          <MText fontWeight="semibold">HubSpot Data Mapping</MText>
          <MText>
            There are some HubSpot data points that are equivalent to
            MonetizeNow data points. You can map the data from HubSpot to
            MonetizeNow for accurate importing.
          </MText>
        </MFlex>
        <MFlex direction="inherit" gap="4">
          {isLoading ? (
            <>
              <MSkeleton height={12} w="full" />
              <MSkeleton height={12} w="full" />
              <MSkeleton height={12} w="full" />
            </>
          ) : (
            <>
              {isHubSpotFieldsError && (
                <CrmFieldsUnavailableAlert crmLabel="HubSpot" />
              )}
              {renderAccordionItems}
            </>
          )}
        </MFlex>
      </MFlex>
      <MFlex gap="4" direction="column">
        <MFlex color="tPurple.dark" direction="inherit" gap="2">
          <MText fontWeight="semibold">MonetizeNow Data Mapping</MText>
          <MText>
            There are some MonetizeNow data points that are equivalent to
            HubSpot data points. You can map the data from MonetizeNow to
            HubSpot for accurate importing.
          </MText>
        </MFlex>
        <MFlex direction="inherit" gap="4">
          {isLoading ? (
            <>
              <MSkeleton height={12} w="full" />
              <MSkeleton height={12} w="full" />
              <MSkeleton height={12} w="full" />
            </>
          ) : (
            <MCard>
              <MCardBody>
                <MFlex gap={4} align="flex-start" justify="space-between">
                  <MFormField label={'MonetizeNow Field'}>
                    <MCustomSelect
                      items={DEAL_AMOUNT_MAPPING_OPTIONS}
                      value={dealAmountMapping}
                      onChange={(value: DealAmountMappingValue) => {
                        setDealAmountMapping(value);
                      }}
                      placeholder="Select field"
                    />
                  </MFormField>
                  <MIcon as={BsArrowRight} mt={9} />
                  <MFormField label={'HubSpot Field'}>
                    <MCustomSelect
                      items={[
                        {
                          title: 'Deal Amount',
                          value: 'dealAmount',
                        },
                      ]}
                      value={'dealAmount'}
                      isDisabled
                    ></MCustomSelect>
                  </MFormField>
                </MFlex>
              </MCardBody>
              <MCardFooter flexDirection={'column'}>
                <MFlex w="full">
                  <MDivider />
                </MFlex>
                <MFlex justify="flex-end" w="full" mt={4}>
                  <MButton
                    variant="primary"
                    type="submit"
                    isLoading={isSavingDealAmountMapping}
                    isDisabled={dealAmountMapping === savedDealAmountMapping}
                    onClick={handleDealAmountMapping}
                  >
                    Save
                  </MButton>
                </MFlex>
              </MCardFooter>
            </MCard>
          )}
          {enableProductCatalogSyncToHubspot &&
            !isLoading &&
            !isEmpty(productToProducts) && (
              <CrmFieldMappingAccordion
                system="hubspot"
                direction="mnToCrm"
                crmObjectType="products"
                crmObjectTypeLabel="HubSpot Product"
                mnObjectType="Product"
                mnObjectTypeLabel="MonetizeNow Product"
                matchedFields={productToProducts}
                userDefinedFields={userDefinedProductToProducts}
                isReadOnly={isHubSpotFieldsError}
              />
            )}
        </MFlex>
      </MFlex>
    </>
  );
};
